import { useAuth } from "application/contexts/auth/AuthContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useUi } from "application/contexts/ui/UIContext";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { useTranslation } from "i18n/useTranslation";
import { IOrder } from "modules/order/domain/models/IOrder";
import { Payment } from "modules/order/domain/models/Payment";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import { ackPayment } from "modules/reactNativeBridge/ReactNativeBridge";
import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { isReactNative } from "utils/constants";
import {
  IRecoverPendingPaymentResult,
  RecoverPendingPaymentUseCase,
} from "../../application/useCases/RecoverPendingPaymentUseCase";
import PendingPaymentModal from "./PendingPaymentModal";

const localOrderRepository = getOrderRepository();
const addLog = getGlobalAddLog();

const RECOVERY_RETRY_MS = 15 * 1000;
/** Com um pagamento pendente na tela: o retorno do m-SiTef deve ser processado assim que chegar */
const RECOVERY_PENDING_RETRY_MS = 2 * 1000;

/** Estado de rota que a recarga offline usa para continuar na gravação do cartão */
export interface IResumeOfflineRechargeState {
  resumeOfflineRechargeOrderId: string;
  payment: Payment;
}

interface IPaymentRecoveryProps {
  orderProvider: RefObject<IOrderProviderRef>;
}

/**
 * Retoma um pagamento m-SiTef aprovado cujo retorno se perdeu (app ou WebView reiniciado com o
 * m-SiTef aberto) do ponto em que parou, como se o retorno tivesse chegado à tela:
 * - fichas: envio, impressão, comprovante, NFC-e e tela de sucesso (onSucessPayment)
 * - recarga online e pós-pago: o mesmo, mais a tela de sucesso da página
 * - recarga offline: o mesmo, depois a tela de gravação no cartão
 * Enquanto houver pagamento pendente, um modal bloqueia o totem para ninguém iniciar outro pedido.
 */
const PaymentRecovery: FC<IPaymentRecoveryProps> = ({ orderProvider }) => {
  const { kiosk } = useKiosk();
  const { isAuth } = useAuth();
  const { toastFullscreen } = useUi();
  const { t } = useTranslation();
  const history = useHistory();
  const running = useRef(false);
  const [blocking, setBlocking] = useState<{ deadline?: number } | null>(null);

  const resume = useCallback(
    async (result: IRecoverPendingPaymentResult) => {
      const order = result.order!;
      const payment = result.payment!;
      const iOrder = order as unknown as IOrder;

      // Saiu do m-SiTef e não chegou ao fim: se já foi enviado ao backend, não reenvia
      if (order.log?.some((it) => it.message === "Pedido enviado")) {
        await localOrderRepository.add({ ...order, payment, paymentApproved: true } as any, "Pagamento recuperado (pedido já enviado antes da queda)");
        await ackPayment(order.id);
        return;
      }

      if (!orderProvider.current) {
        return;
      }

      const recharge = order.rechargeType;
      const isOfflineRecharge = recharge === "Recharge" && order.isOfflineRecharge;
      if (isOfflineRecharge) {
        // A gravação no cartão depende da página da recarga offline: ela retoma a partir do pagamento
        history.replace("/offlineRechargeV2", {
          resumeOfflineRechargeOrderId: order.id,
          payment,
        } as IResumeOfflineRechargeState);
        return;
      }

      history.replace("/");
      await orderProvider.current.resumePayment(iOrder, payment, recharge);
      await ackPayment(order.id);

      if (recharge === "Payment") {
        await localOrderRepository.add({ ...order, payment } as any, "Comanda paga com sucesso");
      }
      if (recharge === "Recharge" || recharge === "Payment") {
        // Tela de sucesso das páginas de recarga online e pós-pago
        toastFullscreen(t("cashless.messages.payment_success"), "success", {
          time: 5000,
          onClose: () => history.replace("/"),
        });
      }
    },
    [history, orderProvider, t, toastFullscreen]
  );

  /** Retorna se ainda há pagamento pendente aguardando o retorno do m-SiTef */
  const recover = useCallback(async () => {
    if (!kiosk || running.current) {
      return false;
    }
    running.current = true;
    try {
      const result = await RecoverPendingPaymentUseCase(localOrderRepository, kiosk);
      if (result.outcome === "wait") {
        // Sem blocking é um pagamento que esta página está conduzindo: o fluxo normal já tem tela
        if (!result.blocking) {
          return false;
        }
        setBlocking({
          deadline: result.remainingMs !== undefined ? Date.now() + result.remainingMs : undefined,
        });
        return true;
      }
      if (result.outcome !== "none") {
        addLog && addLog("PaymentRecovery", { outcome: result.outcome, orderId: result.orderId, message: result.message });
      }
      // O fluxo retomado abre os próprios modais (comprovante, NFC-e, sucesso)
      setBlocking(null);
      if (result.outcome === "approved") {
        await resume(result);
      } else if (result.outcome === "not-approved") {
        history.replace("/");
        toastFullscreen(t("payment_recovery.not_approved"), "error", { time: 5000 });
      }
      return false;
    } catch (error) {
      addLog && addLog("PaymentRecovery", { error });
      setBlocking(null);
      return false;
    } finally {
      running.current = false;
    }
  }, [history, kiosk, resume, t, toastFullscreen]);

  useEffect(() => {
    if (!kiosk || !isAuth || !isReactNative) {
      return;
    }
    // O retorno também pode chegar depois que a página abriu (WebView recarregado com o m-SiTef aberto)
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const loop = async () => {
      const waiting = await recover();
      if (!cancelled) {
        timeout = setTimeout(loop, waiting ? RECOVERY_PENDING_RETRY_MS : RECOVERY_RETRY_MS);
      }
    };
    loop();
    return () => {
      cancelled = true;
      timeout && clearTimeout(timeout);
    };
  }, [kiosk, isAuth, recover]);

  return <PendingPaymentModal open={!!blocking} deadline={blocking?.deadline} />;
};

export default PaymentRecovery;
